import {
  Check,
  CurrencyInr,
  Gift,
  Percent,
  Truck
} from "@phosphor-icons/react";
import { memo, useEffect, useMemo, useRef } from "react";
import { Icon as ReactIcon } from "../@shadcn/components/ui/icon";
import BadgeIcon from "../icons/Badge";
import {
  ProgressBarContext,
  ProgressBarProvider,
  useProgressBar,
} from "./ProgressBarContext";
import { calculateProgress, getTotalProgressSoFar } from "./ProgressHelper";
import {
  Milestone,
  MILESTONE_TYPES,
  MilestoneCategory,
  PROGRESS_BAR_TYPE,
} from "./types";
import { interpolateTemplate, isLightColor } from "./utils";
import { hexToRgba } from "../utils/index";
import { classNames } from "../helpers";

export const MILESTONE_TO_ICON_MAPPER = {
  [MILESTONE_TYPES.FLAT_OFF]: CurrencyInr,
  [MILESTONE_TYPES.FREE_SHIPPING]: Truck,
  [MILESTONE_TYPES.PERCENT_OFF]: Percent,
  [MILESTONE_TYPES.FREEBIE]: Gift,
};

type ProgressBarContainerProps = Pick<
  ProgressBarContext,
  "milestones" | "total" | "customizations" | "onMilestoneReached"
> & {
  children: React.ReactNode;
};

export const ProgressBarContainer = memo(
  ({
    children,
    milestones,
    total,
    customizations,
    onMilestoneReached,
  }: ProgressBarContainerProps) => {
    const previousMilestone = useRef<Milestone | null>();
    const { achievedMilestoneIdx, currentProgress } = useMemo(
      () => calculateProgress(total, milestones, customizations.type),
      [total, milestones, customizations.type]
    );

    useEffect(() => {
      if (
        onMilestoneReached &&
        previousMilestone.current !== milestones[achievedMilestoneIdx]
      ) {
        onMilestoneReached(milestones[achievedMilestoneIdx]!);
      }
      previousMilestone.current = milestones[achievedMilestoneIdx];
    }, [achievedMilestoneIdx]);

    return (
      <ProgressBarProvider
        total={total}
        milestones={milestones}
        achievedMilestoneIdx={achievedMilestoneIdx}
        totalMilestones={milestones.length}
        currentProgress={currentProgress}
        customizations={customizations}
        onMilestoneReached={onMilestoneReached}
      >
        {children}
      </ProgressBarProvider>
    );
  }
);

export const ProgressBarSteps = memo(
  ({ children }: { children: React.ReactNode }) => {
    const { currentProgress, totalMilestones, customizations } =
      useProgressBar();
    const isLight = isLightColor(customizations.filledColor);
    const shadowColor = isLight ? "#ffffff40" : "#00000040";
    const railBackgroundColor = hexToRgba(customizations.filledColor, 0.06);
    const gradientStart = hexToRgba(customizations.filledColor, 0.3);
    const gradientEnd = customizations.filledColor;
    const innerShadowColor = hexToRgba(customizations.filledColor, 0.4);
    const filledBackground = `linear-gradient(90deg, ${gradientStart} 0%, ${gradientEnd} 100%)`;
    const filledShadow = [
      "0px 2px 2px 0px rgba(0, 0, 0, 0.1)",
      "inset 0px 4px 4px 0px rgba(255, 255, 255, 0.35)",
      "inset 0px 0px 0px 0.4px rgba(0, 0, 0, 0.1)",
      `inset -17px 0px 9px -4px ${innerShadowColor}`,
    ].join(", ");
    return (
      <div className="ui-flex ui-flex-col ui-justify-center ui-items-center ui-flex-1 ui-w-[90%]">
        <div
          className={
            "ui-z-10 ui-flex ui-items-center ui-relative ui-flex-1 ui-w-[100%]"
          }
        >
          <div
            style={
              {
                "--progress-bar-filled-color": customizations.filledColor,
                "--progress-bar-shadow-color": shadowColor,
                "--progress-width": `${currentProgress}%`,
                "--progress-height": `8px`,
                "--progress-bar-filled-gradient": filledBackground,
                "--progress-bar-filled-shadow": filledShadow,
                backgroundColor: railBackgroundColor,
                boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.15)",
              } as React.CSSProperties
            }
            className="ui-progress-bar-rail ui-absolute ui-h-[10px] rounded-lg"
          ></div>
          {children}
        </div>
      </div>
    );
  }
);

/**
 * Expected template for milestone_header `Add {{price}} more to get a freebie`
 * {{price}} is replaced with the remaining value
 *
 * this can be extended for other templates variables too.
 * make sure to have no space betweend them
 */
export const ProgressBarHeader = memo(({ index }: { index: number }) => {
  const { achievedMilestoneIdx, milestones, total, customizations } =
    useProgressBar();
  const currentMileStone = milestones[index]!;

  const amount = useMemo(() => {
    const diff = currentMileStone.milestone_amount - total;
    return Number.isInteger(diff) ? diff.toString() : diff.toFixed(2);
  }, [total, currentMileStone]);
  const isCountBased =
    customizations.category === MilestoneCategory.COUNT_BASED;

  const mileStoneHeader: string = useMemo(
    () =>
      interpolateTemplate(currentMileStone.milestone_header)({
        // TODO: remove value once backend changes also removes price
        price: isCountBased ? `${amount}` : `₹${amount}`,
        value: `${amount}`,
      }),
    [amount, currentMileStone]
  );
  if (
    achievedMilestoneIdx === index - 1 &&
    customizations.textonProgressBar_header
  ) {
    return (
      <p
        className="ui-p-3 ui-pt-0 ui-text-center ui-text-text-xs-semibold ui-text-yay-dark"
        style={{ color: customizations.textonProgressBar_headerColor }}
      >
        {mileStoneHeader}
      </p>
    );
  } else if (
    achievedMilestoneIdx === milestones.length - 1 &&
    achievedMilestoneIdx === index &&
    customizations.completedMessage_enabled
  ) {
    return (
      <p
        className="ui-p-3 ui-pt-0 ui-text-center ui-text-text-xs-semibold ui-text-yay-dark"
        style={{ color: customizations.textonProgressBar_headerColor }}
      >
        {customizations.completedMessage}
      </p>
    );
  }
  return <></>;
});

const ProgressBarHeaders = memo(() => {
  const { milestones } = useProgressBar();
  return (
    <>
      {milestones.map((milestone, index) => {
        return <ProgressBarHeader key={milestone.milestone} index={index} />;
      })}
    </>
  );
});

export const ProgressBarStep = memo(({ index }: { index: number }) => {
  const { achievedMilestoneIdx, totalMilestones, milestones, customizations } =
    useProgressBar();
  const { milestone_type, milestone_amount, milestone_text } =
    milestones[index] ?? {};
  const hasAchievedMileStone = achievedMilestoneIdx >= index;
  const nextMilestoneIndex = achievedMilestoneIdx + 1;

  const isCountBased =
    customizations.category === MilestoneCategory.COUNT_BASED;

  const amount = useMemo(
    () => (isCountBased ? milestone_amount : `₹${milestone_amount}`),
    [isCountBased, milestone_amount]
  );

  let IconComponent = Gift;
  if (milestone_type && MILESTONE_TO_ICON_MAPPER[milestone_type]) {
    IconComponent = MILESTONE_TO_ICON_MAPPER[milestone_type];
  }
  // const width = totalMilestones === 1 ? "25%" : 100 / totalMilestones + "%";

  let left = `${getTotalProgressSoFar(totalMilestones, index)}%`;
  if (customizations.type === PROGRESS_BAR_TYPE.DYNAMIC) {
    left = "100%";
  }

  const shouldDimBadge = nextMilestoneIndex !== index && !hasAchievedMileStone;

  const renderAmountText = () => {
    if (isCountBased) {
      return `${amount} Items`;
    }

    return amount;
  };

  return (
    <>
      <div
        style={{
          left,
        }}
        className={`ui-gap-1 ui-text-xxs ui-font-medium ui-absolute ui-flex ui-flex-col ui-items-center -ui-translate-x-1/2`}
      >
        <p
          style={{ color: customizations.textonProgressBar_amountColor, minHeight: 16 }}
          className={classNames("ui-text-text-xxs-semibold", isCountBased ? "ui-text-nowrap" : "")}
        >
          {renderAmountText()}
        </p>
        <div
          className="ui-flex ui-items-center ui-justify-center"
          style={{
            transform:
              nextMilestoneIndex === index
                ? "scale(1)"
                : "scale(1) translateY(0)",
            opacity: shouldDimBadge ? 0.4 : 1,
          }}
        >
          {nextMilestoneIndex === index && (
            <div className="ui-absolute ui-inset-0 ui-flex ui-items-center ui-justify-center custom-ping">
              <BadgeIcon
                color={customizations.filledColor}
                isSolidFill={hasAchievedMileStone}
                strokeWidth={2}
                style={{
                  height: 24,
                  width: 24,
                  overflow: "visible",
                }}
              />
            </div>
          )}
          <BadgeIcon
            color={customizations.filledColor}
            isSolidFill={hasAchievedMileStone}
            style={{
              height: 24,
              width: 24,
              overflow: "visible",
            }}
          />
          {hasAchievedMileStone ? (
            <ReactIcon
              IconComponent={Check}
              size="xs"
              className="ui-absolute ui-text-white -ui-translate-y-0.5"
              iconProps={{
                weight: "bold",
              }}
            />
          ) : (
            <ReactIcon
              IconComponent={IconComponent}
              size="xxs"
              className="ui-absolute -ui-translate-y-0.5"
              iconProps={{
                color: customizations.filledColor,
              }}
            />
          )}
        </div>
        <p
          className="ui-w-full ui-truncate ui-text-center ui-text-text-xxs-medium"
          style={{
            color: customizations.textonProgressBar_titleColor,
            opacity: "70%",
            minHeight: 16
          }}
        >
          {customizations.textonProgressBar_title && milestone_text}
        </p>
      </div>
    </>
  );
});

const StaticProgressBarSteps = memo(() => {
  const { milestones } = useProgressBar();
  return (
    <>
      {milestones.map((milestone, index) => {
        return <ProgressBarStep key={milestone.milestone} index={index} />;
      })}
    </>
  );
});

const DynamicProgressBarSteps = memo(() => {
  const { achievedMilestoneIdx, milestones } = useProgressBar();

  let dynamicMilestoneIdx = achievedMilestoneIdx + 1;
  if (dynamicMilestoneIdx === milestones.length) {
    dynamicMilestoneIdx = achievedMilestoneIdx;
  }

  return <ProgressBarStep index={dynamicMilestoneIdx} />;
});

export const ProgressBar = ({
  total,
  milestones,
  customizations,
  onMilestoneReached,
}: Omit<ProgressBarContainerProps, "children">): React.ReactElement => {
  const steps = useMemo(() => {
    switch (customizations.type) {
      case PROGRESS_BAR_TYPE.DYNAMIC:
        return <DynamicProgressBarSteps />;
      case PROGRESS_BAR_TYPE.STATIC:
        return <StaticProgressBarSteps />;
    }
  }, [customizations.type]);

  const radialGlow = `radial-gradient(120% 140% at 50% 0%, ${hexToRgba(
    customizations.backgroundColor || customizations.filledColor,
    0.3
  )} 0%, transparent 40%)`;
  const outerGradient = `${radialGlow}`;

  if (milestones.length === 0) {
    return <></>;
  }

  return (
    <ProgressBarContainer
      milestones={milestones}
      total={total}
      customizations={customizations}
      onMilestoneReached={onMilestoneReached}
    >
      <div
        style={{
          background: outerGradient,
        }}
        className="ui-p-3 ui-border-t-gray-lighter"
      >
        {(customizations.textonProgressBar_header ||
          customizations.completedMessage_enabled) && <ProgressBarHeaders />}

        <div className="ui-min-h-16 ui-flex ui-flex-col">
          <ProgressBarSteps>{steps}</ProgressBarSteps>
        </div>
      </div>
    </ProgressBarContainer>
  );
};
